/**
 * الروشتات الجاهزة — a doctor's own library of ready-made prescriptions.
 *
 * Most prescribing is repetition: the same six lines for a chest infection, the
 * same four for a blood-pressure follow-up. The autocomplete dictionaries speed
 * up typing one *field*, but they cannot carry a whole script — dose, frequency,
 * timing and duration were retyped every visit even when they never changed.
 *
 * A template is a prescription body with no patient attached. Applying one fills
 * the editor and leaves the doctor to review it; nothing is ever saved to a
 * patient without passing through the normal prescription form, because a
 * template that wrote straight to a record would be a way to prescribe without
 * reading.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Spinner,
  Textarea,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { prescriptionTemplateApi } from '../../api/endpoints';
import { todayKey } from '../../domain/dates';

const emptyMedicine = () => ({
  name: '',
  dosage: '',
  frequency: '',
  timing: '',
  duration: '',
  notes: '',
});

export const emptyTemplate = () => ({
  id: null,
  name: '',
  category: '',
  diagnosis: '',
  notes: '',
  medicines: [emptyMedicine()],
  labTests: [],
  xrays: [],
});

/**
 * A template plus a patient, in the shape the prescription editor expects.
 *
 * Deep-copies every row. Handing the editor the template's own arrays would let
 * an edit to one visit's script mutate the library entry it came from.
 */
export function templateToPrescription(template, doctor, patient = {}) {
  return {
    id: null,
    patientId: patient.patientId ?? null,
    patientName: patient.patientName || '',
    bookingId: patient.bookingId ?? null,
    date: todayKey(),
    diagnosis: template.diagnosis || '',
    notes: template.notes || '',
    medicines: (template.medicines || []).map((medicine) => ({ ...medicine })),
    labTests: (template.labTests || []).map((item) => ({ ...item })),
    xrays: (template.xrays || []).map((item) => ({ ...item })),
    followUpDate: '',
    doctorName: doctor?.name || '',
    doctorTitle: doctor?.title || '',
    doctorLogo: doctor?.logo || '',
    clinicName: patient.clinicName || '',
    clinicAddress: '',
    // Recorded so the list can rank by what a doctor actually reaches for.
    appliedTemplateId: template.id,
  };
}

/**
 * A written prescription, ready to be saved back into the library.
 *
 * Everything patient-specific is dropped: the name, the date, the booking and
 * the follow-up appointment. What is left is the part that repeats.
 */
export function prescriptionToTemplate(prescription) {
  return {
    id: null,
    name: '',
    category: '',
    diagnosis: prescription?.diagnosis || '',
    notes: prescription?.notes || '',
    medicines: (prescription?.medicines || []).map((medicine) => ({ ...medicine })),
    labTests: (prescription?.labTests || []).map((item) => ({ ...item })),
    xrays: (prescription?.xrays || []).map((item) => ({ ...item })),
  };
}

/** Everything a template carries, counted — the summary each card leads with. */
const contentCount = (template) =>
  (template.medicines || []).length +
  (template.labTests || []).length +
  (template.xrays || []).length;

/* ========================================================================== */

export function PrescriptionTemplates({ onApply, draft = null, onDraftHandled }) {
  const { success, error: toastError } = useNotifications();

  const state = useAsync(() => prescriptionTemplateApi.list(), [], { initialData: [] });
  const templates = useMemo(() => state.data || [], [state.data]);

  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [query, setQuery] = useState('');

  // A prescription handed over from the other tab opens straight into the
  // editor, so "save this as a template" is one step rather than a tab switch
  // followed by hunting for a New button.
  useEffect(() => {
    if (!draft) return;
    setEditing(draft);
    onDraftHandled?.();
  }, [draft, onDraftHandled]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter(
      (template) =>
        String(template.name).toLowerCase().includes(needle) ||
        String(template.category || '').toLowerCase().includes(needle) ||
        String(template.diagnosis || '').toLowerCase().includes(needle) ||
        (template.medicines || []).some((medicine) =>
          String(medicine.name).toLowerCase().includes(needle),
        ),
    );
  }, [templates, query]);

  /** Grouped by the doctor's own labels; ungrouped entries come last. */
  const groups = useMemo(() => {
    const buckets = new Map();
    for (const template of filtered) {
      const key = (template.category || '').trim() || '__none__';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(template);
    }
    return [...buckets.entries()].sort(([a], [b]) => {
      if (a === '__none__') return 1;
      if (b === '__none__') return -1;
      return a.localeCompare(b, 'ar');
    });
  }, [filtered]);

  /**
   * Saves, and lets a failure propagate: the editor shows it beside the field,
   * which is where a duplicate-name rejection is actually actionable.
   */
  const handleSave = async (template) => {
    if (template.id) {
      await prescriptionTemplateApi.update(template.id, template);
      success('تم تحديث الروشتة الجاهزة');
    } else {
      await prescriptionTemplateApi.create(template);
      success('تمت إضافة الروشتة الجاهزة');
    }
    setEditing(null);
    state.reload();
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      await prescriptionTemplateApi.remove(removing.id);
      success('تم حذف الروشتة الجاهزة');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر الحذف');
    } finally {
      setRemoving(null);
    }
  };

  const handleApply = (template) => {
    onApply(template);
    // Fire-and-forget: failing to record a use is not worth interrupting the
    // doctor mid-consultation, and the count is only a sort key.
    prescriptionTemplateApi.recordUse(template.id).then(
      () => state.reload(),
      () => {},
    );
  };

  return (
    <div className="space-y-5">
      <Panel className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث بالاسم أو التشخيص أو اسم الدواء..."
            className="pr-9"
          />
        </div>

        <Button icon="plus" onClick={() => setEditing(emptyTemplate())}>
          روشتة جاهزة جديدة
        </Button>
      </Panel>

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && templates.length === 0 ? (
        <Spinner label="جاري تحميل الروشتات الجاهزة..." />
      ) : templates.length === 0 ? (
        <Panel>
          <EmptyState
            icon="clipboard-list"
            title="لا توجد روشتات جاهزة"
            message="احفظ الروشتات التي تكتبها كثيراً لتطبّقها بضغطة واحدة بدل إعادة كتابة الجرعات."
            action={
              <Button icon="plus" onClick={() => setEditing(emptyTemplate())}>
                أضف أول روشتة جاهزة
              </Button>
            }
          />
        </Panel>
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState icon="search-x" title="لا نتائج" message="جرّب كلمة بحث أخرى." />
        </Panel>
      ) : (
        groups.map(([category, entries]) => (
          <section key={category} className="space-y-3">
            <h3 className="flex items-center gap-2 px-1 text-sm font-bold text-slate-300">
              <Icon
                name={category === '__none__' ? 'folder' : 'folder-open'}
                size={15}
                className="text-purple-400"
              />
              {category === '__none__' ? 'بدون تصنيف' : category}
              <span className="text-xs font-normal text-slate-500">({entries.length})</span>
            </h3>

            <div className="grid gap-3 lg:grid-cols-2">
              {entries.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onApply={() => handleApply(template)}
                  onEdit={() => setEditing(template)}
                  onRemove={() => setRemoving(template)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <TemplateEditor
        template={editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
        title="حذف الروشتة الجاهزة"
        confirmLabel="حذف"
        message={`سيتم حذف "${removing?.name || ''}" من مكتبتك. الروشتات التي كُتبت بها لن تتأثر.`}
      />
    </div>
  );
}

/* ========================================================================== */

function TemplateCard({ template, onApply, onEdit, onRemove }) {
  const medicines = template.medicines || [];

  return (
    <Panel className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="shrink-0 rounded-xl bg-purple-500/10 p-2.5 text-purple-400">
          <Icon name="clipboard-list" size={18} />
        </span>

        <div className="min-w-0 flex-1">
          <h4 className="truncate font-bold text-white">{template.name}</h4>
          {template.diagnosis && (
            <p className="truncate text-xs text-slate-400">{template.diagnosis}</p>
          )}

          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {medicines.length > 0 && (
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="pill">
                {medicines.length} دواء
              </Badge>
            )}
            {(template.labTests || []).length > 0 && (
              <Badge
                className="border-amber-500/30 bg-amber-500/10 text-amber-300"
                icon="flask-conical"
              >
                {template.labTests.length} تحليل
              </Badge>
            )}
            {(template.xrays || []).length > 0 && (
              <Badge className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300" icon="scan">
                {template.xrays.length} أشعة
              </Badge>
            )}
            {template.useCount > 0 && (
              <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="repeat">
                استُخدمت {template.useCount} مرة
              </Badge>
            )}
          </div>
        </div>
      </div>

      {medicines.length > 0 && (
        <ul className="space-y-1 rounded-xl border border-white/5 bg-white/5 p-2.5">
          {medicines.slice(0, 4).map((medicine, index) => (
            <li key={index} className="flex flex-wrap gap-x-2 text-[11px] text-slate-400">
              <span className="font-bold text-slate-200">{medicine.name}</span>
              {medicine.dosage && <span>{medicine.dosage}</span>}
              {medicine.frequency && <span>· {medicine.frequency}</span>}
              {medicine.duration && <span>· {medicine.duration}</span>}
            </li>
          ))}
          {medicines.length > 4 && (
            <li className="text-[11px] text-slate-500">
              و{medicines.length - 4} دواء آخر…
            </li>
          )}
        </ul>
      )}

      <div className="mt-auto flex flex-wrap justify-end gap-2 border-t border-white/5 pt-3">
        <Button
          variant="subtle"
          icon="trash-2"
          className="!text-red-300"
          onClick={onRemove}
          aria-label={`حذف ${template.name}`}
        >
          حذف
        </Button>
        <Button variant="ghost" icon="pencil" onClick={onEdit}>
          تعديل
        </Button>
        <Button icon="wand-sparkles" onClick={onApply} disabled={contentCount(template) === 0}>
          استخدام
        </Button>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function TemplateEditor({ template, onClose, onSave }) {
  const [form, setForm] = useState(template);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(template);
    setError(null);
  }, [template]);

  if (!form) return null;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError(null);
  };

  const updateMedicine = (index, field, value) =>
    setForm((current) => {
      const medicines = [...(current.medicines || [])];
      medicines[index] = { ...medicines[index], [field]: value };
      return { ...current, medicines };
    });

  const updateItem = (key, index, field, value) =>
    setForm((current) => {
      const list = [...(current[key] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, [key]: list };
    });

  const submit = async () => {
    const cleaned = {
      ...form,
      name: String(form.name || '').trim(),
      medicines: (form.medicines || []).filter((m) => String(m.name || '').trim()),
      labTests: (form.labTests || []).filter((t) => String(t.name || '').trim()),
      xrays: (form.xrays || []).filter((x) => String(x.name || '').trim()),
    };

    if (!cleaned.name) {
      setError('اسم الروشتة الجاهزة مطلوب');
      return;
    }
    if (contentCount(cleaned) === 0 && !String(cleaned.diagnosis || '').trim()) {
      setError('أضف دواءً أو تحليلاً أو تشخيصاً على الأقل');
      return;
    }

    setSaving(true);
    try {
      await onSave(cleaned);
    } catch (err) {
      // 409 is a duplicate name — a correctable mistake, so it belongs next to
      // the field rather than in a toast that scrolls away.
      setError(err.message || 'تعذر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل روشتة جاهزة' : 'روشتة جاهزة جديدة'}
      icon="clipboard-list"
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          {error && <span className="text-xs font-bold text-red-400">{error}</span>}
          <div className="mr-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button icon="save" loading={saving} onClick={submit}>
              حفظ
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="اسم الروشتة الجاهزة" required hint="مثال: نزلة برد">
            <Input value={form.name} onChange={update('name')} autoFocus />
          </Field>

          <Field label="التصنيف" hint="اختياري — لتجميع المكتبة">
            <Input
              value={form.category || ''}
              onChange={update('category')}
              placeholder="جهاز تنفسي"
            />
          </Field>
        </div>

        <Field label="التشخيص">
          <Input value={form.diagnosis || ''} onChange={update('diagnosis')} />
        </Field>

        {/* Medicines */}
        <section className="space-y-3 border-t border-white/10 pt-5">
          <h4 className="flex items-center gap-2 text-sm font-bold text-cyan-300">
            <Icon name="pill" size={15} />
            الأدوية
          </h4>

          {(form.medicines || []).map((medicine, index) => (
            <div key={index} className="space-y-3 rounded-xl border border-white/5 bg-white/5 p-3">
              <div className="flex items-center gap-2">
                <Input
                  value={medicine.name}
                  onChange={(event) => updateMedicine(index, 'name', event.target.value)}
                  placeholder="اسم الدواء"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      medicines: current.medicines.filter((_, i) => i !== index),
                    }))
                  }
                  className="shrink-0 rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
                  aria-label="حذف الدواء"
                >
                  <Icon name="trash-2" size={15} />
                </button>
              </div>

              <div className="grid gap-2 sm:grid-cols-4">
                <Input
                  value={medicine.dosage}
                  onChange={(event) => updateMedicine(index, 'dosage', event.target.value)}
                  placeholder="الجرعة"
                />
                <Input
                  value={medicine.frequency}
                  onChange={(event) => updateMedicine(index, 'frequency', event.target.value)}
                  placeholder="عدد المرات"
                />
                <Input
                  value={medicine.timing}
                  onChange={(event) => updateMedicine(index, 'timing', event.target.value)}
                  placeholder="التوقيت"
                />
                <Input
                  value={medicine.duration}
                  onChange={(event) => updateMedicine(index, 'duration', event.target.value)}
                  placeholder="المدة"
                />
              </div>

              <Input
                value={medicine.notes}
                onChange={(event) => updateMedicine(index, 'notes', event.target.value)}
                placeholder="ملاحظات (اختياري)"
              />
            </div>
          ))}

          <Button
            variant="subtle"
            icon="plus"
            onClick={() =>
              setForm((current) => ({
                ...current,
                medicines: [...(current.medicines || []), emptyMedicine()],
              }))
            }
          >
            إضافة دواء
          </Button>
        </section>

        <ItemList
          title="التحاليل المطلوبة"
          icon="flask-conical"
          items={form.labTests || []}
          onAdd={() =>
            setForm((c) => ({ ...c, labTests: [...(c.labTests || []), { name: '', notes: '' }] }))
          }
          onRemove={(index) =>
            setForm((c) => ({ ...c, labTests: c.labTests.filter((_, i) => i !== index) }))
          }
          onChange={(index, field, value) => updateItem('labTests', index, field, value)}
        />

        <ItemList
          title="الأشعة المطلوبة"
          icon="scan"
          items={form.xrays || []}
          onAdd={() => setForm((c) => ({ ...c, xrays: [...(c.xrays || []), { name: '', notes: '' }] }))}
          onRemove={(index) =>
            setForm((c) => ({ ...c, xrays: c.xrays.filter((_, i) => i !== index) }))
          }
          onChange={(index, field, value) => updateItem('xrays', index, field, value)}
        />

        <section className="border-t border-white/10 pt-5">
          <Field label="ملاحظات للمريض">
            <Textarea
              value={form.notes || ''}
              onChange={update('notes')}
              rows={3}
              placeholder="تعليمات تتكرر مع هذه الحالة"
            />
          </Field>
        </section>
      </div>
    </Modal>
  );
}

/* ========================================================================== */

function ItemList({ title, icon, items, onAdd, onRemove, onChange }) {
  return (
    <section className="space-y-3 border-t border-white/10 pt-5">
      <h4 className="flex items-center gap-2 text-sm font-bold text-cyan-300">
        <Icon name={icon} size={15} />
        {title}
      </h4>

      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            value={item.name}
            onChange={(event) => onChange(index, 'name', event.target.value)}
            placeholder="الاسم"
            className="flex-1"
          />
          <Input
            value={item.notes || ''}
            onChange={(event) => onChange(index, 'notes', event.target.value)}
            placeholder="ملاحظات"
            className="flex-1"
          />
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="shrink-0 rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
            aria-label={`حذف من ${title}`}
          >
            <Icon name="trash-2" size={15} />
          </button>
        </div>
      ))}

      <Button variant="subtle" icon="plus" onClick={onAdd}>
        إضافة
      </Button>
    </section>
  );
}

export default PrescriptionTemplates;
